/**
 * Deterministic shuffle utility for survey answer options.
 * Uses a seeded PRNG (Park-Miller) so that:
 * - Different users see different orders (seed includes unique session/user data)
 * - The same user sees a stable order within one session (same seed = same result)
 */

function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash) || 1; // Ensure non-zero
}

/**
 * Shuffle an array deterministically using a seed string.
 * Uses Fisher-Yates algorithm with Park-Miller PRNG.
 * @param {Array} array - The array to shuffle
 * @param {string} seed - Seed string for deterministic randomness
 * @returns {Array} A new shuffled array (original is not mutated)
 */
export function seededShuffle(array, seed) {
  if (!array || array.length <= 1) return array;
  
  const shuffled = [...array];
  let currentSeed = hashCode(seed);
  
  const random = () => {
    currentSeed = (currentSeed * 16807) % 2147483647;
    return (currentSeed - 1) / 2147483646;
  };
  
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
}

/**
 * Get or create a unique session ID for shuffle stability.
 * Persists in sessionStorage so it stays consistent during one survey session.
 */
function getShuffleSessionId() {
  let sessionId = sessionStorage.getItem('survey_shuffle_session');
  if (!sessionId) {
    sessionId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    sessionStorage.setItem('survey_shuffle_session', sessionId);
  }
  return sessionId;
}

/**
 * Get shuffled options for a question if randomizeOptions is enabled.
 * Returns the original array if randomization is not enabled.
 * @param {Object} question - The question object
 * @param {string} surveyId - The survey ID
 * @returns {Array} options or imageOptions array, shuffled if randomizeOptions is true
 */
export function getShuffledOptions(question, surveyId) {
  if (!question.randomizeOptions) {
    return question.options || [];
  }
  
  const sessionId = getShuffleSessionId();
  const seed = `${surveyId}_${question.id}_${sessionId}`;
  return seededShuffle(question.options || [], seed);
}

/**
 * Get shuffled imageOptions for a question if randomizeOptions is enabled.
 * @param {Object} question - The question object
 * @param {string} surveyId - The survey ID
 * @returns {Array} imageOptions array, shuffled if randomizeOptions is true
 */
export function getShuffledImageOptions(question, surveyId) {
  if (!question.randomizeOptions) {
    return question.imageOptions || [];
  }
  
  const sessionId = getShuffleSessionId();
  const seed = `${surveyId}_${question.id}_${sessionId}`;
  return seededShuffle(question.imageOptions || [], seed);
}
